import { CSSProperties, Fragment, useEffect, useRef, useState } from 'react';
import { Color, Size, Styles } from '@vivli/shared/theme';
import { PopoutMenuView, PopoutPopoutRenderer, PopoutTargetRenderer } from '@vivli/shared/components';
import { AssetsConstant, DTIActiveUserMenu } from '@vivli/shared/infrastructure/constants';
import { IPageLink } from '@vivli/shared/infrastructure/interface';

const popoutTargetContainerStyle = (isOpen: boolean): CSSProperties => ({
    ...Styles.NO_SELECT,
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    height: Size.HEADER_HEIGHT,
    backgroundColor: isOpen ? Color.VIVLI_ACTIVE_BLUE : null,
    transition: `background-color ${Size.HOVER_TRANSITION_TIME}s`
});

const popoutTargetImgContainerStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    paddingLeft: Size.PADDING,
    paddingRight: Size.PADDING,
    borderTopRightRadius: Size.BORDER_RADIUS,
    borderBottomRightRadius: Size.BORDER_RADIUS,
    borderTopLeftRadius: Size.BORDER_RADIUS,
    borderBottomLeftRadius: Size.BORDER_RADIUS,
    ...Styles.Transition.HOVER_OPACITY_TRANSITION
};

const roleTextStyle: CSSProperties = {
    marginLeft: Size.PADDING / 2,
    marginRight: Size.PADDING / 2,
    fontSize: '14px',
    fontWeight: '545',
    color: Color.WHITE,
    textTransform: 'uppercase'
};

const popoutContainerStyle = (isHover: boolean, hasSeparator?: boolean): CSSProperties => ({
    height: Size.HEADER_HEIGHT / 2,
    paddingBottom: Size.PADDING,
    paddingLeft: Size.PADDING,
    paddingRight: Size.PADDING,
    paddingTop: Size.PADDING,
    fontSize: Size.FontSize.Medium,
    fontWeight: Size.FontWeight.Bold,
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    position: 'relative',
    zIndex: 2000,
    color: isHover ? Color.VIVLI_LIGHT_BLUE : Color.DARK_GRAY,
    backgroundColor: isHover ? Color.HOVER_LIGHT_BLUE : 'transparent',
    borderTopColor: hasSeparator ? Color.SEPARATOR_LIGHT_BLUE : 'transparent',
    borderTopWidth: hasSeparator ? 1 : 0,
    borderTopStyle: 'solid',
    ...Styles.Transition.HOVER_OPACITY_TRANSITION
});

interface ActiveUserViewProps {
    actionRequested?: (urlTarget?: string, commandTarget?: Function) => void; //send my parent a request to navigate to a page or execute a command
    userRoleName?: string;
    isPartner?: boolean; //if the user is a partner, they have different options in the menu,
    links?: IPageLink[]; //page links to management pages
    defaultOpen?: boolean;
}

export const ActiveUserMenuView = ({ actionRequested, links, userRoleName, defaultOpen = false }: ActiveUserViewProps) => {
    const [menuIsOpen, setMenuIsOpen] = useState(false);
    const hasAutoOpenedRef = useRef(false);

    useEffect(() => {
        if (!defaultOpen || hasAutoOpenedRef.current) {
            return;
        }

        hasAutoOpenedRef.current = true;
        setMenuIsOpen(true);
    }, [defaultOpen]);

    const handleActionRequested = (url?: string, command?: Function) => {
        actionRequested && actionRequested(url, command);
    };

    //this is the button that is always visible in the header, and when clicked opens the menu with the options.
    // It shows the user role and an avatar icon, and changes style when the menu is open
    const popoutTargetRenderer: PopoutTargetRenderer = (isOpen) => {
        const PopoutTarget = () => (
            <div
                style={popoutTargetContainerStyle(isOpen)}
                className="main_menu"
                data-test-id={DTIActiveUserMenu.MainMenu}
            >
                <div key="avatar-container" style={popoutTargetImgContainerStyle}>
                    <img src={AssetsConstant.avatarIconTransparent} alt="User menu" style={{ width: '20px' }} />
                    <div style={roleTextStyle}>{userRoleName}</div>
                    <img src={AssetsConstant.downArrowWhiteIcon} alt="Open user menu" style={{ width: '12px' }} />
                </div>
            </div>
        );

        return <PopoutTarget />;
    };

    // this is the menu that pops out when the button is clicked, it shows the options that the user can click on.
    // It uses the links and commands passed in as props to render the menu items
    const popoutPopoutRenderer: PopoutPopoutRenderer = (style, onItemSelected) => {
        const MenuItem = ({
            title,
            url,
            command,
            onClick,
            dataId,
            topSeparator
        }: {
            title?: string;
            url?: string;
            command?: Function;
            onClick: () => void;
            dataId?: string;
            topSeparator?: boolean;
        }) => {
            const [isHover, setIsHover] = useState(false);
            return (
                <Fragment>
                    <div
                        style={popoutContainerStyle(isHover, topSeparator)}
                        className={'menuItem_' + (title ? title.replace(/[^\w]/gi, '_') : 'empty')}
                        onClick={() => {
                            onClick();

                            onItemSelected();
                            handleActionRequested(url, command);
                            setMenuIsOpen(false);
                        }}
                        onMouseEnter={() => setIsHover(true)}
                        onMouseLeave={() => setIsHover(false)}
                        data-test-id={dataId}
                    >
                        {title}
                    </div>
                </Fragment>
            );
        };

        return (
            <div
                style={{
                    ...style,
                    backgroundColor: Color.WHITE,
                    boxShadow: Size.MENU_BOX_SHADOW,
                    width: '100%',
                    borderTopRightRadius: Size.BORDER_RADIUS,
                    borderBottomRightRadius: Size.BORDER_RADIUS,
                    borderTopLeftRadius: Size.BORDER_RADIUS,
                    borderBottomLeftRadius: Size.BORDER_RADIUS
                }}
            >
                {links?.map(({ title, url, defaultTab, dataId, command, separateGroup }, index) => (
                    <MenuItem
                        key={index}
                        title={title}
                        onClick={() => {
                            handleActionRequested(url, command);
                        }}
                        dataId={dataId}
                        topSeparator={separateGroup}
                    />
                ))}
            </div>
        );
    };

    return (
        <div
            style={{
                borderTopRightRadius: Size.BORDER_RADIUS,
                borderBottomRightRadius: Size.BORDER_RADIUS,
                borderTopLeftRadius: Size.BORDER_RADIUS,
                borderBottomLeftRadius: Size.BORDER_RADIUS
            }}
        >
            <PopoutMenuView
                popoutRenderer={popoutPopoutRenderer}
                targetRenderer={popoutTargetRenderer}
                isOpen={menuIsOpen}
                setIsOpen={setMenuIsOpen}
                onClickOutside={() => setMenuIsOpen(false)}
                disableReposition
            />
        </div>
    );
};
